import { useIntl } from 'react-intl'

import useAuthStore, { getAuthUser } from 'app/store/auth.store'
import { defaultCurrency } from 'app/utils/constants/intl.constants'
import { USER_ROLE } from 'app/utils/constants/role.constants'

interface UsePriceProps {
  listingCurrency?: Currency
  actualPrice: number
  convertedActualPrice: number
  convertedTradePrice: number
  isAuction?: boolean
  isHighestBidder: boolean
  isOutbid: boolean
  userCurrency?: string
  tradePrice?: number
}

export const usePrice = ({
  listingCurrency,
  actualPrice,
  convertedActualPrice,
  convertedTradePrice,
  userCurrency,
  isAuction,
  isHighestBidder,
  isOutbid,
  tradePrice
}: UsePriceProps) => {
  const intl = useIntl()
  const user = useAuthStore(getAuthUser)
  const isBusinessAccount = user?.role === USER_ROLE.BUSINESS

  const actualPriceWithCurrency = intl.formatNumber(actualPrice, {
    currency: listingCurrency?.name || defaultCurrency,
    style: 'currency'
  })

  const convertedActualPriceWithCurrency = intl.formatNumber(
    convertedActualPrice,
    {
      currency: userCurrency,
      style: 'currency'
    }
  )

  const convertedTradePriceWithCurrency = intl.formatNumber(
    convertedTradePrice,
    {
      currency: userCurrency,
      style: 'currency'
    }
  )

  const businessApproxConvertedPrice = !!convertedTradePrice
    ? convertedTradePriceWithCurrency
    : convertedActualPriceWithCurrency
  const approxConvertedPrice = isBusinessAccount
    ? businessApproxConvertedPrice
    : convertedActualPriceWithCurrency

  const showApprox =
    listingCurrency?.name !== userCurrency &&
    (!!convertedActualPrice || !!convertedTradePrice)

  const showBidLabel = isAuction && (isHighestBidder || isOutbid)

  const isTradePrice = !!tradePrice

  const showPersonalPrice =
    !isBusinessAccount || (isBusinessAccount && !isTradePrice)
  const showBusinessTradePrice = isBusinessAccount && isTradePrice

  return {
    actualPriceWithCurrency,
    approxConvertedPrice,
    showApprox,
    showBidLabel,
    showPersonalPrice,
    showBusinessTradePrice,
    isTradePrice
  }
}
